// Transactions · shared pipeline column builder.
//
// Shared column builder for the pipeline row chrome. Used by every queue tab
// whose body is the generic `DefaultTabBody` — passed via the tab's
// `dataColumns:` slot. Returns already-projected `DataColumn<Transaction>[]`
// (since `auditColumns()` is helper-built, not schema-driven).
//
// Pipeline-specific fields (reference_number_pipeline, checklist_progress,
// docs, emails, transport, etc.) live in `transactionPipelineFields.tsx`.
// Plain scalar columns are sourced from `transactionPresentation` via
// `toListColumn`. Relation columns (product, load/unload place) come from
// `transactionPresentation` with the FK key names.
//
// `reference_number_pipeline` is keyed `reference_number` at the column
// level so preset `defaultColumns` arrays (which always referenced the
// legacy `"reference_number"` key) keep matching without rewrites.

import type { Transaction } from "@shared/types/transaction";
import type { DataColumn } from "@ds/tables/types";
import { toListColumn } from "@ds/config/presentationRefs";
import { transactionPresentation } from "../transactionConfig";
import { pipelineFields } from "../transactionPipelineFields";

/** Cancelled transactions tint red. */
export const transactionRowTone = (record: Transaction): "red" | null =>
  record.status_override === "cancelled" ? "red" : null;

/**
 * Logistics-scoped row tone: cancelled tints red (wins), else priority tints
 * amber. Used only on the logistics surfaces (logistics pipeline + logistics
 * task queue) — the shared `transactionRowTone` stays cancelled-only so sales
 * + admin queues never leak orange.
 */
export const logisticsRowTone = (record: Transaction): "red" | "amber" | null =>
  record.status_override === "cancelled" ? "red" : record.priority ? "amber" : null;

/**
 * Zdjęcia-tab row tone (issue #295). Same precedence as `logisticsRowTone` —
 * cancelled red, then priority amber — with a third rank below them: a load
 * happening TODAY tints blue, which is what replaces the issue's two
 * "ZAŁADUNKI DZIŚ" / "ZAŁADUNKI WCZEŚNIEJ" sections in a single date-ordered
 * table. Ranked last so a cancelled or priority row loading today keeps the
 * signal it already had.
 *
 * `todayIso` is injectable for tests; production callers omit it so the tint is
 * recomputed per render and never goes stale in a long-lived tab.
 */
export const logisticsPhotoRowTone = (
  record: Transaction,
  todayIso: string = new Date().toISOString().slice(0, 10),
): "red" | "amber" | "blue" | null =>
  logisticsRowTone(record) ?? (record.load_date === todayIso ? "blue" : null);

/**
 * Full pipeline column catalog — every available column, remapped to its
 * canonical key, in natural order. The bare-path `buildPipelineColumns`
 * filters this down to a preset's visible subset; the managed-table path
 * (column manager) consumes the whole catalog and lets the chooser pick.
 */
export function pipelineColumnCatalog(): DataColumn<Transaction>[] {
  const schemaCols = [
    pipelineFields.reference_number_pipeline,
    pipelineFields.checklist_progress,
    { ...toListColumn(transactionPresentation.load_date, { width: 120, sorter: true, sortField: "effective_load_date" }),
      edit: { key: "load_date", field: { type: "date" } },
      access: { editableTo: ["admin", "developer", "administration", "logistics"] } },
    { ...toListColumn(transactionPresentation.unload_date, { width: 120, sorter: true }),
      edit: { key: "unload_date", field: { type: "date" } },
      access: { editableTo: ["admin", "developer", "administration", "logistics"] } },
    toListColumn(transactionPresentation.load_time, { width: 100 }),
    toListColumn(transactionPresentation.unload_time, { width: 100 }),
    { ...toListColumn(transactionPresentation.loaded_weight), props: { size: "xs" } },
    pipelineFields.copy_docs,
    pipelineFields.original_docs,
    pipelineFields.customs_docs,
    pipelineFields.photos,
    pipelineFields.generated_docs,
    pipelineFields.unload_weight,
    pipelineFields.number_of_bales_pipeline,
    pipelineFields.unload_price_pipeline,
    pipelineFields.load_price_pipeline,
    pipelineFields.unload_confirmation,
    pipelineFields.logistics_emails,
    pipelineFields.transport_action,
    pipelineFields.generated_docs_transport_order,
    pipelineFields.logistics_emails_transport_order,
    pipelineFields.carriers,
    pipelineFields.transport_price,
    pipelineFields.drivers,
    pipelineFields.vehicles,
    pipelineFields.load_invoice,
    pipelineFields.unload_invoice,
    pipelineFields.transport_invoice,
    pipelineFields.logistics_employee_assign,
    pipelineFields.days_since_unload,
    pipelineFields.days_since_delivery,
    pipelineFields.last_reminder_sent,
    pipelineFields.send_reminder,
  ] as unknown as DataColumn<Transaction>[];

  // Remap pipeline-suffixed keys back to the canonical names preset
  // `defaultColumns` arrays use (legacy keys). Without this remap the
  // `*_pipeline` columns are filtered out by the visibility step below.
  const KEY_REMAP: Record<string, { key: string; width?: number; pinned?: "left" | "right" }> = {
    reference_number_pipeline:   { key: "reference_number", width: 180, pinned: "left" },
    unload_price_pipeline:       { key: "unload_price" },
    load_price_pipeline:         { key: "load_price" },
    number_of_bales_pipeline:    { key: "number_of_bales" },
  };
  for (let i = 0; i < schemaCols.length; i += 1) {
    const c = schemaCols[i];
    const r = KEY_REMAP[c.key];
    if (r) {
      schemaCols[i] = { ...c, ...r } as DataColumn<Transaction>;
    }
  }

  // Relation columns: presentation derives the column key from the FK
  // column (`product_id`, `load_place_company_id`, …). Preset `defaultColumns`
  // arrays use the legacy short names (`product`, `load_place`, `unload_place`).
  // Remap the column key here so the visibility filter below matches.
  const productCol = {
    ...toListColumn(transactionPresentation.product_id),
    key: "product",
  } as DataColumn<Transaction>;
  const loadPlaceCol = {
    ...toListColumn(transactionPresentation.load_place_company_id),
    key: "load_place",
  } as DataColumn<Transaction>;
  const unloadPlaceCol = {
    ...toListColumn(transactionPresentation.unload_place_company_id),
    key: "unload_place",
  } as DataColumn<Transaction>;

  return [
    ...schemaCols,
    productCol,
    loadPlaceCol,
    unloadPlaceCol,
    toListColumn(transactionPresentation.created_at, { width: 140, sorter: true, defaultVisible: false }) as unknown as DataColumn<Transaction>,
    toListColumn(transactionPresentation.updated_at, { width: 140, sorter: true, defaultVisible: false }) as unknown as DataColumn<Transaction>,
  ];
}

export function buildPipelineColumns(
  defaultColumns: readonly string[],
): DataColumn<Transaction>[] {
  const cols = pipelineColumnCatalog();

  // Legacy DataTable v2 honoured `data.defaultColumns` as the per-tab
  // visible subset; the rest of the catalog was reachable via the column
  // chooser. The bare-path QueueShell renders the Table primitive directly
  // with no chooser, so "visible" == "rendered". Filter the catalog down to
  // the preset's list, preserving the preset's declared order.
  const order = new Map(defaultColumns.map((k, i) => [k, i] as const));
  return cols
    .filter((c) => order.has(c.key))
    .sort((a, b) => (order.get(a.key) ?? 999) - (order.get(b.key) ?? 999));
}
